import { X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import type {
  AlfredBulkImportJobStatus,
  AlfredBulkImportProgressJob,
} from "@/types/alfred-bulk-import"

interface AlfredBulkImportProgressProps {
  jobId: string
  job?: AlfredBulkImportProgressJob
  error?: unknown
  isLoading: boolean
  onDismiss: () => void
}

const TRAILING_JOB_NUMBER = /(\d+)$/

const STATUS_LABELS: Record<AlfredBulkImportJobStatus, string> = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  COMPLETED: "Completed",
  COMPLETED_WITH_ERRORS: "Completed with errors",
  FAILED: "Failed",
}

export function AlfredBulkImportProgress({
  jobId,
  job,
  error,
  isLoading,
  onDismiss,
}: AlfredBulkImportProgressProps) {
  const jobNumber = TRAILING_JOB_NUMBER.exec(jobId)?.[1] ?? jobId
  const percentage = job?.totalRows
    ? Math.min(100, Math.round((job.processed / job.totalRows) * 100))
    : 0
  const statusLabel = job ? STATUS_LABELS[job.status] : isLoading ? "Loading" : "Pending"
  const statusVariant =
    job?.status === "COMPLETED"
      ? "success"
      : job?.status === "FAILED" || job?.status === "COMPLETED_WITH_ERRORS"
      ? "destructive"
      : "secondary"
  const allErrors = job?.errorSample ?? []

  return (
    <section
      aria-label="Alfred bulk import job"
      aria-live="polite"
      className="border-t border-border bg-card/80"
    >
      <div className="container mx-auto flex flex-col gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-foreground">
            Alfred bulk import Job {jobNumber}
          </p>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
          {job ? (
            <p className="text-xs text-muted-foreground">
              {job.processed} / {job.totalRows} processed
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Waiting for job progress…</p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto"
            aria-label="Dismiss bulk import progress"
            onClick={onDismiss}
          >
            <X data-icon="inline-start" />
            Dismiss
          </Button>
        </div>

        <Progress
          value={percentage}
          className="h-2"
          aria-label="Alfred bulk import progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percentage}
          aria-valuetext={job ? `${job.processed} of ${job.totalRows} rows processed` : "Waiting"}
        />

        {job ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>{job.succeeded} succeeded</span>
            <span>{job.failed} failed</span>
          </div>
        ) : null}

        {error ? (
          <p role="status" className="text-xs text-destructive">
            Unable to refresh progress. Retrying…
          </p>
        ) : null}

        {allErrors.length > 0 ? (
          <div className="flex flex-col gap-1 text-xs">
            <p className="font-medium text-foreground">Failure summary</p>
            <ScrollArea
              role="region"
              aria-label="Bulk import failure details"
              className="h-40 rounded-md border border-border p-2 pr-4"
            >
              <ul className="flex flex-col gap-1 text-muted-foreground">
                {allErrors.map((sample) => {
                  const identity =
                    sample.clientId && sample.deviceId
                      ? `${sample.clientId} / ${sample.deviceId}`
                      : sample.clientId ?? sample.deviceId

                  return (
                    <li
                      key={`${sample.rowNumber}-${sample.clientId ?? "client"}-${sample.deviceId ?? "device"}-${sample.error}`}
                    >
                      Row {sample.rowNumber}
                      {identity ? ` · ${identity}` : ""}: {sample.error}
                    </li>
                  )
                })}
              </ul>
            </ScrollArea>
            {job && job.failed > allErrors.length ? (
              <p className="text-muted-foreground">
                Showing {allErrors.length} of {job.failed} failures returned by the API.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}
